// The client engine for conflict-free note sync (see "Client engine" in
// PLAN.md): a note's unsent edits are kept as text operations and rebased
// over changes this client did not make, in the ot.js client shape. Pure;
// callers persist the state. Reference for the Swift and Kotlin ports, which
// run the same noteContentSync.fixtures.json. Change all three or none.
//
// Invariant: content === apply(apply(base, inflight?.ops ?? identity), buffer)

import { OpBuilder, apply, compose, diff, hashText, identityOp, isIdentityOp, transform, type TextOp } from "./textOps"
import { assembleFrontmatter, mergeFrontmatter, splitFrontmatterBlock } from "./frontmatterMerge"

export interface InflightChange {
  clientId: string
  seq: number
  baseRev: number
  /** base -> base∘inflight */
  ops: TextOp
}

export interface NoteSyncState {
  /** The server rev `base` is at (0 = unknown). */
  rev: number
  /** Server text at `rev` (null = no common ancestor). */
  base: string | null
  baseUpdatedAt: string
  /** The local text. */
  content: string
  inflight: InflightChange | null
  /** base∘inflight -> content */
  buffer: TextOp
}

export interface SyncDevice {
  clientId: string
  nextSeq: number
}

export type ContentRequest =
  | { kind: "catchUp"; rev: number }
  | { kind: "submit"; baseRev: number; baseHash: string; clientId: string; seq: number; ops: TextOp }

export interface RemoteChange {
  rev: number
  clientId: string
  seq: number
  ops: TextOp
  at: string
}

export interface AppliedReply {
  seq: number
  rev: number
  at: string
}

export interface PullSnapshot {
  rev: number
  text: string
  at: string
}

export interface ContentSnapshot extends PullSnapshot {
  yourLastSeq: number
  reset?: boolean
}

export interface RebaseResult {
  /** newBase -> content' */
  pending: TextOp
  content: string
  /** content -> content' */
  editorOps: TextOp
}

export interface SnapshotResult {
  state: NoteSyncState
  editorOps: TextOp[]
  /** The local text with no common base, to save as a new private note. */
  orphan?: string
}

export function initialSyncState({
  rev,
  base,
  baseUpdatedAt = "",
  content,
}: {
  rev: number
  base: string | null
  baseUpdatedAt?: string
  content: string
}): NoteSyncState {
  const b = base ?? null
  return {
    rev,
    base: b,
    baseUpdatedAt,
    content,
    inflight: null,
    buffer: b === null ? identityOp(content.length) : diff(b, content),
  }
}

export function localEdit(s: NoteSyncState, text: string): NoteSyncState {
  if (text === s.content) return s
  return { ...s, content: text, buffer: compose(s.buffer, diff(s.content, text)) }
}

export function prepareSubmit(
  s: NoteSyncState,
  d: SyncDevice,
): { state: NoteSyncState; device: SyncDevice; request: ContentRequest | null } {
  // Without a base the client holds no server text at any rev: ask from 0 (a snapshot).
  if (s.rev === 0 || s.base === null) return { state: s, device: d, request: { kind: "catchUp", rev: s.base === null ? 0 : s.rev } }
  if (s.inflight) {
    const f = s.inflight
    return {
      state: s,
      device: d,
      request: { kind: "submit", baseRev: f.baseRev, baseHash: hashText(s.base), clientId: f.clientId, seq: f.seq, ops: f.ops },
    }
  }
  if (isIdentityOp(s.buffer)) return { state: s, device: d, request: null }
  const inflight: InflightChange = { clientId: d.clientId, seq: d.nextSeq, baseRev: s.rev, ops: s.buffer }
  return {
    state: { ...s, inflight, buffer: identityOp(s.content.length) },
    device: { ...d, nextSeq: d.nextSeq + 1 },
    request: {
      kind: "submit",
      baseRev: s.rev,
      baseHash: hashText(s.base),
      clientId: d.clientId,
      seq: inflight.seq,
      ops: inflight.ops,
    },
  }
}

// Does `op` (on `base`) change anything inside base's frontmatter block, or leave a different block?
function touchesFrontmatter(base: string, op: TextOp, result: string): boolean {
  const fb = splitFrontmatterBlock(base)
  if (splitFrontmatterBlock(result).inner !== fb.inner) return true
  if (fb.inner === null) return false
  // "---\n" + inner + "\n---\n": exactly where splitFrontmatterBlock puts the body.
  const end = fb.inner.length + 9
  let i = 0
  for (const c of op) {
    if (i >= end) break
    if (typeof c === "string" || c < 0) return true
    i += c
  }
  return false
}

/** The part of `op` acting on base[from..]: retains and deletes clipped at `from`, inserts kept at or after it. */
export function sliceOp(op: TextOp, from: number): TextOp {
  const out: TextOp = []
  let i = 0
  const push = (c: number | string) => {
    const l = out.length
    const last = out[l - 1]
    if (typeof c === "number" && typeof last === "number" && Math.sign(last) === Math.sign(c)) out[l - 1] = last + c
    else out.push(c)
  }
  for (const c of op) {
    if (typeof c === "string") {
      if (i >= from) push(c)
      continue
    }
    const n = Math.abs(c)
    const sign = Math.sign(c)
    const start = Math.max(i, from)
    const end = i + n
    if (end > start) push(sign * (end - start))
    i = end
  }
  return out
}

function concatOps(a: TextOp, b: TextOp): TextOp {
  const r = new OpBuilder()
  for (const c of [...a, ...b]) {
    if (typeof c === "string") r.insert(c)
    else if (c > 0) r.retain(c)
    else r.delete(-c)
  }
  return r.ops
}

/** pending: base -> content. remote: base -> newBase. */
export function rebaseOps(base: string, content: string, pending: TextOp, newBase: string, remote: TextOp): RebaseResult {
  if (touchesFrontmatter(base, pending, content) && touchesFrontmatter(base, remote, newBase)) {
    // Rare: both changed the block. Body by the ops sliced to it, the block structurally.
    const fb = splitFrontmatterBlock(base)
    const fl = splitFrontmatterBlock(content)
    const fr = splitFrontmatterBlock(newBase)
    const from = base.length - fb.body.length
    let bl = sliceOp(pending, from)
    let br = sliceOp(remote, from)
    if (apply(fb.body, bl) !== fl.body) bl = diff(fb.body, fl.body)
    if (apply(fb.body, br) !== fr.body) br = diff(fb.body, fr.body)
    const [bodyL] = transform(bl, br)
    const body = apply(fr.body, bodyL)
    const merged = assembleFrontmatter(mergeFrontmatter(fb.inner, fl.inner, fr.inner), body)
    const oldBlock = newBase.slice(0, newBase.length - fr.body.length)
    const newBlock = merged.slice(0, merged.length - body.length)
    return { pending: concatOps(diff(oldBlock, newBlock), bodyL), content: merged, editorOps: diff(content, merged) }
  }
  const [pendingP, remoteP] = transform(pending, remote)
  return { pending: pendingP, content: apply(content, remoteP), editorOps: remoteP }
}

function pendingOf(s: NoteSyncState): TextOp {
  return s.inflight ? compose(s.inflight.ops, s.buffer) : s.buffer
}

export function receiveChange(
  s: NoteSyncState,
  ch: RemoteChange,
): { state: NoteSyncState; editorOps: TextOp[]; needsCatchUp: boolean } {
  if (ch.rev <= s.rev) return { state: s, editorOps: [], needsCatchUp: false }
  if (ch.rev !== s.rev + 1 || s.base === null) return { state: s, editorOps: [], needsCatchUp: true }
  const newBase = apply(s.base, ch.ops)
  if (s.inflight && s.inflight.clientId === ch.clientId && s.inflight.seq === ch.seq) {
    return {
      state: { ...s, rev: ch.rev, base: newBase, baseUpdatedAt: ch.at, inflight: null },
      editorOps: [],
      needsCatchUp: false,
    }
  }
  // A foreign change: the in-flight change can never apply now (its baseRev is behind). Fold it into the buffer.
  const r = rebaseOps(s.base, s.content, pendingOf(s), newBase, ch.ops)
  return {
    state: { ...s, rev: ch.rev, base: newBase, baseUpdatedAt: ch.at, content: r.content, inflight: null, buffer: r.pending },
    editorOps: [r.editorOps],
    needsCatchUp: false,
  }
}

export function receiveChanges(
  s: NoteSyncState,
  changes: RemoteChange[],
): { state: NoteSyncState; editorOps: TextOp[]; needsCatchUp: boolean } {
  let state = s
  let editorOps: TextOp[] = []
  let needsCatchUp = false
  for (const ch of changes) {
    const r = receiveChange(state, ch)
    state = r.state
    editorOps = editorOps.concat(r.editorOps)
    if (r.needsCatchUp) {
      needsCatchUp = true
      break
    }
  }
  return { state, editorOps, needsCatchUp }
}

export function receiveApplied(s: NoteSyncState, { seq, rev, at }: AppliedReply): { state: NoteSyncState; needsCatchUp: boolean } {
  if (!s.inflight || s.inflight.seq !== seq || s.base === null) return { state: s, needsCatchUp: false }
  if (rev !== s.rev + 1) return { state: s, needsCatchUp: true }
  return { state: { ...s, rev, base: apply(s.base, s.inflight.ops), baseUpdatedAt: at, inflight: null }, needsCatchUp: false }
}

/**
 * Never feed it a catch-up snapshot while a change is in flight: the server
 * reads `yourLastSeq` from the catch-up's own clientId, and a snapshot that
 * left before the in-flight change landed would rebase it as unsent and send
 * it twice. Catch up only between submits (a submit's own snapshot reply is
 * fine: it is about that change).
 */
export function receiveSnapshot(s: NoteSyncState, snap: ContentSnapshot): SnapshotResult {
  if (s.base !== null && snap.rev <= s.rev && !snap.reset) return { state: s, editorOps: [] }
  if (s.base === null) {
    const st: NoteSyncState = {
      ...s,
      rev: snap.rev,
      base: snap.text,
      baseUpdatedAt: snap.at,
      inflight: null,
      buffer: identityOp(snap.text.length),
    }
    if (s.content === snap.text) return { state: st, editorOps: [] }
    return { state: { ...st, content: snap.text }, editorOps: [diff(s.content, snap.text)], orphan: s.content }
  }
  let base1 = s.base
  let pending = pendingOf(s)
  if (s.inflight && snap.yourLastSeq >= s.inflight.seq) {
    base1 = apply(s.base, s.inflight.ops)
    pending = s.buffer
  }
  const r = rebaseOps(base1, s.content, pending, snap.text, diff(base1, snap.text))
  return {
    state: { ...s, rev: snap.rev, base: snap.text, baseUpdatedAt: snap.at, content: r.content, inflight: null, buffer: r.pending },
    editorOps: [r.editorOps],
  }
}

export function receivePullSnapshot(s: NoteSyncState, snap: PullSnapshot): SnapshotResult {
  if (s.inflight) return { state: s, editorOps: [] }
  if (s.base !== null && snap.rev <= s.rev) return { state: s, editorOps: [] }
  return receiveSnapshot(s, { ...snap, yourLastSeq: 0 })
}

/** Throws when the invariant is broken; the caller repairs and reports it. */
export function checkSyncState(s: NoteSyncState): void {
  if (s.base === null) return
  const mid = s.inflight ? apply(s.base, s.inflight.ops) : s.base
  if (apply(mid, s.buffer) !== s.content) throw new Error("note sync invariant broken")
}

/** For writers that only have the text they want (server writers, stale editor saves). */
export function rebase(base: string, content: string, newBase: string, remote: TextOp): RebaseResult {
  return rebaseOps(base, content, diff(base, content), newBase, remote)
}
